/**
 * A safe wrapper around localStorage and sessionStorage that handles
 * SecurityError/QuotaExceededError and falls back to an in-memory store.
 * Critical for mobile Safari Private Mode.
 */

type StorageType = 'local' | 'session';

class SafeStorage {
  private memoryStore: Record<string, string> = {};
  private type: StorageType;

  constructor(type: StorageType) {
    this.type = type;
  }

  private get storage(): Storage | null {
    try {
      return this.type === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
      return null;
    }
  }

  getItem(key: string): string | null {
    try {
      const s = this.storage;
      return s ? s.getItem(key) : (this.memoryStore[key] || null);
    } catch {
      return this.memoryStore[key] || null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      const s = this.storage;
      if (s) {
        s.setItem(key, value);
      } else {
        this.memoryStore[key] = value;
      }
    } catch {
      this.memoryStore[key] = value;
    }
  }

  removeItem(key: string): void {
    try {
      const s = this.storage;
      if (s) {
        s.removeItem(key);
      } else {
        delete this.memoryStore[key];
      }
    } catch {
      delete this.memoryStore[key];
    }
  }

  clear(): void {
    try {
      const s = this.storage;
      if (s) {
        s.clear();
      } else {
        this.memoryStore = {};
      }
    } catch {
      this.memoryStore = {};
    }
  }

  get length(): number {
    try {
      const s = this.storage;
      return s ? s.length : Object.keys(this.memoryStore).length;
    } catch {
      return Object.keys(this.memoryStore).length;
    }
  }
}

export const safeLocalStorage = new SafeStorage('local');
export const safeSessionStorage = new SafeStorage('session');
